<template>
<BaseDialog
	ref="dialog"
	data-component="PromptDialog.vue"
	:modelValue="modelValue"
	:historyMode="historyMode"
	:type="type"
	:closeOnOverlay="closeOnOverlay"
	:contentClass="contentClass"
	@update:model-value="$emit('update:modelValue', $event)"
	@dismissed="$emit('dismissed', $event)"
	@opened="$emit('opened')"
	@closed="$emit('closed')"
>
	<div class="flex flex-col gap-3 text-sm sm:text-base">
		<OIcon
			v-if="icon"
			class="mx-auto sm:mx-0"
			:class="color === 'danger' ? 'text-danger' : 'text-info'"
			:name="icon"
			:size="2.5"
		/>
		<div v-if="title" class="text-xl font-bold" v-html="title"/>
		<div v-if="body" v-html="body"/>
		<slot/>
	</div>
	<div
		class="mx-auto mb-2 mt-6 flex flex-col gap-2 sm:mb-0 sm:w-full sm:flex-row-reverse"
		:class="fullWidthButtons ? 'w-full' : 'w-max'"
	>
		<OButton
			v-if="confirmText"
			data-test="prompt_dialog__confirm"
			:color="color"
			:label="confirmText"
			:disabled="confirmDisabled"
			fullWidthUntil="sm"
			@click.prevent="emitAction('confirmed', 'button')"
		/>
		<OButton
			v-if="secondaryText"
			data-test="prompt_dialog__confirm_secondary"
			class="sm:ml-auto"
			:color="color"
			variant="outlined"
			:label="secondaryText"
			:disabled="secondaryDisabled"
			:icon="secondaryIcon"
			fullWidthUntil="sm"
			@click.prevent="emitAction('confirmed-secondary', 'button')"
		/>
		<OButton
			v-if="dismissText"
			data-test="prompt_dialog__dissmised"
			:color="color"
			:variant="isDismissButtonBorder ? 'outlined' : 'ghost'"
			:label="dismissText"
			fullWidthUntil="sm"
			@click.prevent="emitAction('dismissed', 'button')"
		/>
	</div>
</BaseDialog>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import BaseDialog from './BaseDialog.vue';

/*
	Okno dialogowe z pytaniem oraz przyciskami.
*/
export default defineComponent({
	name: 'PromptDialog',
	components: {
		BaseDialog,
	},
	props: {
		/**
		 * Czy modal ma być wyświetlony?
		 */
		modelValue: {
			type: Boolean,
			default: false,
		},

		/**
		 * Zamiast aktualizować model przy wywołaniu akcji, wywołaj `window.history.back()`.
		 */
		historyMode: {
			type: Boolean,
			default: true,
		},

		/**
		 * Czy zamknąć okno po kliknięciu na nakładkę?
		 */
		closeOnOverlay: {
			type: Boolean,
			default: true,
		},

		/**
		 * Tytuł pytania
		 */
		title: {
			type: String,
			default: '',
		},

		/**
		 * Treść pytania
		 */
		body: {
			type: String,
			default: '',
		},

		/**
		 * Treść przycisku potwierdzającego
		 */
		confirmText: {
			type: String,
			default: 'OK',
		},

		/**
		 * Czy przycisk potwierdzający jest zablokowany?
		 */
		confirmDisabled: {
			type: Boolean,
			default: false,
		},

		/**
		 * Treść przycisku pomocniczego (jeżeli puste - nie wyświetlaj)
		 */
		secondaryText: {
			type: String,
			default: '',
		},

		/**
		 * Ikona przycisku pomocniczego
		 */
		secondaryIcon: {
			type: String,
			default: '',
		},

		/**
		 * Czy przycisk potwierdzający jest zablokowany?
		 */
		secondaryDisabled: {
			type: Boolean,
			default: false,
		},

		/**
		 * Treść przycisku anulującego dialog (może mieć inne działanie
		 * niż inny sposób zamknięcia okna, poprzez sprawdzenie parametru akcji `@dimissed`)
		 */
		dismissText: {
			type: String,
			default: 'Anuluj',
		},

		/**
		 * Czy dismissButton ma mieć ramke?
		 */
		isDismissButtonBorder: {
			type: Boolean,
			default: false,
		},

		/**
		 * Typ okna dialogowego
		 */
		type: {
			type: String,
			default: 'default',
		},

		/*
		 * Klasy, które powinny być zastosowane do wewnętrznego kontenera
		 */
		contentClass: {
			type: String,
			default: '',
		},

		/**
		 * Czy przyciski powinny rozciągnąć się na całą szerokość
		 */
		fullWidthButtons: {
			type: Boolean,
			default: false,
		},

		/**
		 * Czy dialog powinien zostać zamknięty po wykonaniu akcji
		 */
		autoClose: {
			type: Boolean,
			default: true,
		},

		/**
		 * Kolor przycisków
		 */
		color: {
			type: String as PropType<'info' | 'danger'>,
			default: 'info',
		},

		/**
		 * Opcjonalna ikona
		 */
		icon: {
			type: String,
			default: '',
		},
	},
	emits: [
		'update:modelValue',
		'dismissed',
		'confirmed',
		'confirmed-secondary',
		'opened',
		'closed',
	],
	methods: {
		/**
		 * Po kliknięciu przycisku wysyła odpowiednie zdarzenie (`confirmed`, `confirmed-secondary`, `dismissed`)
		 * @param name Typ przycisku
		 * @param source Źródło kliknięcia (przycisk, krzyżyk, nakładka)
		 */
		emitAction(
			name: 'dismissed' | 'confirmed' | 'confirmed-secondary',
			source: 'button' | 'window' | 'overlay' = 'button',
		) {
			this.$emit(name, source);
			if (this.autoClose || name === 'dismissed') {
				this.close();
			}
		},

		/**
		 * Zamyka okno dialogowe: w zależności od właściwościwości `historyMode`, ustawia
		 */
		close() {
			(this.$refs.dialog as InstanceType<typeof BaseDialog>).close();
		},
	},
});
</script>
